import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, RefreshCw, DollarSign } from 'lucide-react';
import AdminPageHeader from '../../components/admin/AdminPageHeader';
import * as aiBillingApi from '../../api/aiBilling';
import UpgradePlanModal from '../../components/billing/UpgradePlanModal';

const planBadges = {
  none: 'bg-gray-100 text-gray-600',
  starter: 'bg-blue-50 text-blue-700',
  pro: 'bg-purple-50 text-purple-700',
  unlimited: 'bg-amber-50 text-amber-700',
};

function usageBarColor(percent) {
  if (percent >= 95) return 'bg-red-500';
  if (percent >= 80) return 'bg-orange-500';
  if (percent >= 60) return 'bg-yellow-500';
  return 'bg-green-500';
}

export default function AIBillingDashboard() {
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState({ history: [], total: 0, page: 1, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [detailed, hist] = await Promise.all([
        aiBillingApi.getUsageDetailed(),
        aiBillingApi.getBillingHistory(1),
      ]);
      setStats(detailed);
      setHistory(hist);
    } catch (err) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadHistoryPage = async (page) => {
    setHistoryLoading(true);
    try {
      const h = await aiBillingApi.getBillingHistory(page);
      setHistory(h);
    } catch (err) { /* ignore */ }
    setHistoryLoading(false);
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">AI Usage & Billing</h1>
        </div>
        <div className="text-center py-16 text-gray-400">Loading billing data...</div>
      </div>
    );
  }

  const sub = stats?.subscription;
  const isNone = sub?.plan_name === 'none';
  const isUnlimited = sub?.queries_limit === null;

  // Format dates
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <AdminPageHeader title="AI Usage & Billing" description="Monitor your AI assistant usage and manage your plan" icon={DollarSign} iconColor="text-teal-600" iconBg="bg-teal-50">
        <button onClick={() => { setLoading(true); loadAll(); }} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50">
          <RefreshCw className="h-4 w-4" />
        </button>
      </AdminPageHeader>

      {/* ════════════════════════════════════════ */}
      {/* None plan banner */}
      {/* ════════════════════════════════════════ */}
      {isNone && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center mb-8">
          <span className="text-4xl mb-3 block">🤖</span>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">AI features are not enabled</h2>
          <p className="text-sm text-gray-500 mb-4">Upgrade your plan to unlock AI-powered case analysis, document review, and more.</p>
          <button onClick={() => setShowUpgrade(true)}
            className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors">
            Enable AI Assistant
          </button>
        </div>
      )}

      {/* ════════════════════════════════════════ */}
      {/* Section 1: Plan & Cycle Summary Cards */}
      {/* ════════════════════════════════════════ */}
      {!isNone && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Card 1: Current Plan */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs text-gray-500 mb-1">Current Plan</p>
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              <span className="text-lg font-bold text-gray-900">{sub?.plan_display_name}</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">${Number(sub?.price_per_month || 0).toFixed(0)}/mo</p>
          </div>

          {/* Card 2: Billing Cycle */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs text-gray-500 mb-1">Billing Cycle</p>
            <p className="text-lg font-bold text-gray-900">{fmtDate(sub?.billing_cycle_start)} — {fmtDate(sub?.billing_cycle_end)}</p>
            <p className="text-sm text-gray-500 mt-1">Resets in {sub?.days_remaining || 0} day{sub?.days_remaining !== 1 ? 's' : ''}</p>
          </div>

          {/* Card 3: Quick Ask Usage */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs text-gray-500 mb-1">Quick Ask</p>
            {isUnlimited ? (
              <>
                <p className="text-lg font-bold text-gray-900">{sub?.queries_used || 0} <span className="text-sm font-normal text-gray-400">queries used</span></p>
              </>
            ) : (
              <>
                <p className="text-lg font-bold text-gray-900">{sub?.queries_used || 0} <span className="text-sm font-normal text-gray-400">/ {sub?.queries_limit}</span></p>
                <div className="mt-2">
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${usageBarColor(sub?.quick_ask_percent || 0)}`}
                      style={{ width: `${sub?.quick_ask_percent || 0}%` }} />
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5">{sub?.quick_ask_percent || 0}% used</p>
                </div>
              </>
            )}
          </div>

          {/* Card 4: Deep Analysis Usage */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs text-gray-500 mb-1">Deep Analysis</p>
            {sub?.deep_analyses_limit === null ? (
              <p className="text-lg font-bold text-gray-900">{sub?.deep_analyses_used || 0} <span className="text-sm font-normal text-gray-400">analyses used</span></p>
            ) : (
              <>
                <p className="text-lg font-bold text-gray-900">{sub?.deep_analyses_used || 0} <span className="text-sm font-normal text-gray-400">/ {sub?.deep_analyses_limit}</span></p>
                <div className="mt-2">
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${usageBarColor(sub?.deep_analysis_percent || 0)}`}
                      style={{ width: `${sub?.deep_analysis_percent || 0}%` }} />
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5">{sub?.deep_analysis_percent || 0}% used</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════ */}
      {/* Section 2: Usage Trend Chart (CSS bars) */}
      {/* ════════════════════════════════════════ */}
      {!isNone && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Daily Usage — Last 30 Days</h2>
          <DailyUsageChart data={stats?.dailyTrend || []} cycleStart={sub?.billing_cycle_start} />
        </div>
      )}

      {/* ════════════════════════════════════════ */}
      {/* Section 3: Per-User Breakdown */}
      {/* ════════════════════════════════════════ */}
      {!isNone && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900">Usage by Team Member</h2>
          </div>
          {(stats?.userBreakdown || []).length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-gray-400">No queries this billing cycle</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                    <th className="px-6 py-3">User</th>
                    <th className="px-6 py-3">Email</th>
                    <th className="px-6 py-3 text-right">Quick Asks</th>
                    <th className="px-6 py-3 text-right">Deep Analyses</th>
                    <th className="px-6 py-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {stats.userBreakdown.map((u, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-6 py-3 font-medium text-gray-900">{u.first_name} {u.last_name}</td>
                      <td className="px-6 py-3 text-gray-500">{u.email}</td>
                      <td className="px-6 py-3 text-right text-gray-700">{u.quick_asks || 0}</td>
                      <td className="px-6 py-3 text-right text-gray-700">{u.deep_analyses || 0}</td>
                      <td className="px-6 py-3 text-right font-medium text-gray-900">{u.total_queries || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════ */}
      {/* Section 4: Change Plan */}
      {/* ════════════════════════════════════════ */}
      {!isNone && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
          <h2 className="text-sm font-semibold text-gray-900 mb-1">Change Plan</h2>
          <p className="text-xs text-gray-500 mb-4">Upgrade your plan for more AI queries and deep analyses</p>
          <PlanCards currentPlan={sub?.plan_name} onUpgradeClick={() => setShowUpgrade(true)} />
        </div>
      )}

      {/* ════════════════════════════════════════ */}
      {/* Section 5: Billing History */}
      {/* ════════════════════════════════════════ */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Billing History</h2>
        </div>
        {history.history.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-gray-400">No billing history yet</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                    <th className="px-6 py-3">Date</th>
                    <th className="px-6 py-3">Action</th>
                    <th className="px-6 py-3">Plan</th>
                    <th className="px-6 py-3">Details</th>
                    <th className="px-6 py-3">Performed By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.history.map(h => (
                    <tr key={h.id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-gray-600 whitespace-nowrap">{new Date(h.created_at).toLocaleDateString()}</td>
                      <td className="px-6 py-3"><ActionBadge action={h.action} /></td>
                      <td className="px-6 py-3 text-gray-700">{h.new_plan_display_name || h.old_plan_display_name || '—'}</td>
                      <td className="px-6 py-3 text-xs text-gray-500">
                        {h.action === 'plan_change' && h.old_plan_display_name
                          ? `${h.old_plan_display_name} → ${h.new_plan_display_name}`
                          : h.action === 'cycle_reset'
                          ? `${h.queries_at_change} queries, ${h.deep_analyses_at_change} deep`
                          : '—'}
                      </td>
                      <td className="px-6 py-3 text-gray-600">
                        {h.performed_by_first ? `${h.performed_by_first} ${h.performed_by_last}` : 'System'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {history.totalPages > 1 && (
              <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-500">{history.total} total entries</span>
                <div className="flex gap-1">
                  <button disabled={history.page <= 1 || historyLoading} onClick={() => loadHistoryPage(history.page - 1)}
                    className="text-xs px-2 py-1 border border-gray-200 rounded disabled:opacity-40">Prev</button>
                  <span className="text-xs px-2 py-1 text-gray-500">Page {history.page} of {history.totalPages}</span>
                  <button disabled={history.page >= history.totalPages || historyLoading} onClick={() => loadHistoryPage(history.page + 1)}
                    className="text-xs px-2 py-1 border border-gray-200 rounded disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Upgrade modal */}
      <UpgradePlanModal
        isOpen={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        currentPlan={sub?.plan_name || 'none'}
        onUpgraded={() => { setLoading(true); loadAll(); }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════
// Daily Usage Chart (CSS bars — no library needed)
// ═══════════════════════════════════════════

function fmtChartDate(dateStr) {
  // Parse YYYY-MM-DD without timezone shift: extract parts directly
  const [, m, d] = dateStr.split('-').map(Number);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return months[m - 1] + ' ' + d;
}

function DailyUsageChart({ data, cycleStart }) {
  // Pad all days from billing cycle start to today with zero values
  const filledData = React.useMemo(() => {
    if (!cycleStart) return data || [];
    const dataMap = {};
    for (const d of (data || [])) {
      const key = d.date.split('T')[0]; // 'YYYY-MM-DD' from ISO string
      dataMap[key] = d;
    }
    // Use cycleStart date string directly — no Date object needed for the start key
    const startKey = String(cycleStart).split('T')[0];
    const result = [];
    // Walk from cycle start to today using local dates
    const start = new Date(startKey + 'T12:00:00'); // noon avoids DST edge cases
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    for (let dt = new Date(start); dt <= today; dt.setDate(dt.getDate() + 1)) {
      const key = dt.getFullYear() + '-' + String(dt.getMonth() + 1).padStart(2, '0') + '-' + String(dt.getDate()).padStart(2, '0');
      result.push(dataMap[key] || { date: key, quick_asks: 0, deep_analyses: 0 });
    }
    return result;
  }, [data, cycleStart]);

  if (!filledData || filledData.length === 0) {
    return <p className="text-sm text-gray-400 text-center py-6">No usage data this billing cycle</p>;
  }

  // Find max daily total for scaling
  const maxVal = Math.max(1, ...filledData.map(d => (parseInt(d.quick_asks) || 0) + (parseInt(d.deep_analyses) || 0)));
  const chartHeight = 140;

  return (
    <div>
      {/* Legend */}
      <div className="flex items-center gap-4 mb-3 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-blue-400" /> Quick Ask</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-purple-400" /> Deep Analysis</span>
      </div>
      {/* Chart */}
      <div className="flex items-end gap-[2px]" style={{ height: chartHeight + 'px' }}>
        {filledData.map((d, i) => {
          const qa = parseInt(d.quick_asks) || 0;
          const da = parseInt(d.deep_analyses) || 0;
          const total = qa + da;
          const qaHeight = qa > 0 ? Math.max(2, Math.round((qa / maxVal) * chartHeight)) : 0;
          const daHeight = da > 0 ? Math.max(2, Math.round((da / maxVal) * chartHeight)) : 0;
          const dateStr = fmtChartDate(d.date.split('T')[0]);

          return (
            <div key={i} className="flex-1 flex flex-col items-stretch justify-end group relative" style={{ minWidth: 0 }}>
              {/* Tooltip */}
              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block z-10 whitespace-nowrap">
                <div className="bg-gray-800 text-white text-[10px] px-2 py-1 rounded shadow-lg">
                  {dateStr}: {qa} quick, {da} deep ({total} total)
                </div>
              </div>
              {/* Stacked bars */}
              {da > 0 && <div className="bg-purple-400 rounded-t-sm transition-all" style={{ height: daHeight + 'px' }} />}
              {qa > 0 && <div className={`bg-blue-400 transition-all ${da > 0 ? '' : 'rounded-t-sm'}`} style={{ height: qaHeight + 'px' }} />}
              {total === 0 && <div className="bg-gray-100 rounded-t-sm" style={{ height: '2px' }} />}
            </div>
          );
        })}
      </div>
      {/* X-axis labels — show first, middle, last */}
      <div className="flex justify-between mt-1">
        <span className="text-[10px] text-gray-400">{fmtChartDate(filledData[0]?.date.split('T')[0])}</span>
        {filledData.length > 2 && (
          <span className="text-[10px] text-gray-400">{fmtChartDate(filledData[Math.floor(filledData.length / 2)]?.date.split('T')[0])}</span>
        )}
        {filledData.length > 1 && (
          <span className="text-[10px] text-gray-400">{fmtChartDate(filledData[filledData.length - 1]?.date.split('T')[0])}</span>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Plan Cards (inline, reuses upgrade modal for actual change)
// ═══════════════════════════════════════════

const PLAN_ORDER = ['starter', 'pro', 'unlimited'];
const planMeta = {
  starter: { emoji: '⚡', border: 'border-blue-200', bg: 'bg-blue-50', badge: 'text-blue-700 bg-blue-100' },
  pro: { emoji: '🚀', border: 'border-purple-200', bg: 'bg-purple-50', badge: 'text-purple-700 bg-purple-100' },
  unlimited: { emoji: '💎', border: 'border-amber-200', bg: 'bg-amber-50', badge: 'text-amber-700 bg-amber-100' },
};

function PlanCards({ currentPlan, onUpgradeClick }) {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    aiBillingApi.getPlans()
      .then(d => setPlans((d.plans || []).filter(p => p.name !== 'none')))
      .catch(() => {});
  }, []);

  const currentIdx = PLAN_ORDER.indexOf(currentPlan);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {plans.map(plan => {
        const meta = planMeta[plan.name] || planMeta.starter;
        const isCurrent = currentPlan === plan.name;
        const planIdx = PLAN_ORDER.indexOf(plan.name);
        const isLower = planIdx <= currentIdx && !isCurrent;

        return (
          <div key={plan.id} className={`rounded-xl border p-4 ${isCurrent ? 'border-gray-300 bg-gray-50' : isLower ? 'border-gray-200 bg-gray-50 opacity-60' : meta.border + ' ' + meta.bg}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">{meta.emoji}</span>
              <div>
                <h3 className="text-sm font-bold text-gray-900">{plan.display_name}</h3>
                <span className="text-xs text-gray-500">${Number(plan.price_per_month).toFixed(0)}/mo</span>
              </div>
            </div>
            <div className="text-xs text-gray-600 space-y-1 mb-3">
              <p>✓ {plan.monthly_query_limit === null ? 'Unlimited' : plan.monthly_query_limit} queries/mo</p>
              <p>✓ {plan.monthly_deep_analysis_limit === null ? 'Unlimited' : plan.monthly_deep_analysis_limit} deep analyses/mo</p>
            </div>
            {isCurrent ? (
              <span className={`inline-block px-3 py-1 rounded-lg text-xs font-medium ${meta.badge}`}>Current Plan</span>
            ) : isLower ? (
              <span className="inline-block px-3 py-1 rounded-lg text-xs font-medium text-gray-400 bg-gray-100">—</span>
            ) : (
              <button onClick={onUpgradeClick}
                className="w-full px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                Upgrade
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════
// Action Badge
// ═══════════════════════════════════════════

function ActionBadge({ action }) {
  const styles = {
    plan_change: 'bg-green-50 text-green-700',
    cycle_reset: 'bg-blue-50 text-blue-700',
    limit_reached: 'bg-red-50 text-red-700',
    plan_assigned: 'bg-gray-100 text-gray-600',
  };
  const labels = {
    plan_change: 'Upgraded',
    cycle_reset: 'Cycle Reset',
    limit_reached: 'Limit Reached',
    plan_assigned: 'Assigned',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${styles[action] || 'bg-gray-100 text-gray-600'}`}>
      {labels[action] || action}
    </span>
  );
}
